import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Icon } from "@/lib/icons";

export default function Navbar({ site }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const branding = site?.branding || {};
  const menu = site?.menu || [];
  const cta = site?.navCta || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (!href) return;
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 400);
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-[var(--c-bg)]/85 backdrop-blur-xl border-b border-[var(--c-border)] shadow-[0_4px_30px_rgba(106,27,26,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[74px] flex items-center justify-between">
        <button onClick={() => go("#top")} data-testid="logo" className="flex items-center gap-3 group">
          <span className="grid place-items-center h-11 w-11 rounded-full border border-[var(--c-accent)] text-[var(--c-primary)] group-hover:bg-[var(--c-primary)] group-hover:text-white transition-colors">
            <Icon name="Mic" className="h-5 w-5" />
          </span>
          <span className="text-left leading-none">
            <span className="block font-heading text-2xl font-bold text-[var(--c-primary)]">{branding.name || "শ্রুতি"}</span>
            <span className="block font-body text-[10px] tracking-[0.35em] text-[var(--c-accent)]">{branding.sub || "SHRUTI"}</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {menu.map((m, i) => (
            <button
              key={i}
              data-testid={`nav-link-${i}`}
              onClick={() => go(m.href)}
              className="font-body text-[15px] text-[var(--c-text)] hover:text-[var(--c-secondary)] transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 hover:after:w-full after:bg-[var(--c-secondary)] after:transition-all"
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {cta.label && (
            <button
              data-testid="nav-cta"
              onClick={() => go(cta.href)}
              className="hidden sm:inline-flex items-center rounded-full px-6 py-2.5 font-body font-medium text-white bg-[var(--c-primary)] hover:bg-[var(--c-secondary)] transition-colors hover:-translate-y-0.5 duration-300"
            >
              {cta.label}
            </button>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden grid place-items-center h-11 w-11 rounded-full border border-[var(--c-border)] text-[var(--c-primary)]"
            aria-label="মেনু"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[var(--c-bg)] border-b border-[var(--c-border)] px-5 py-4 space-y-1">
          {menu.map((m, i) => (
            <button
              key={i}
              onClick={() => go(m.href)}
              className="block w-full text-right font-body text-lg py-2.5 text-[var(--c-text)] hover:text-[var(--c-secondary)] border-b border-[var(--c-border)]/60 last:border-0"
            >
              {m.label}
            </button>
          ))}
          {cta.label && (
            <button
              onClick={() => go(cta.href)}
              className="mt-3 w-full rounded-full px-6 py-3 font-body font-medium text-white bg-[var(--c-primary)]"
            >
              {cta.label}
            </button>
          )}
        </div>
      )}
    </header>
  );
}
